/**
 * Moment-aware adaptation — types and pure computation.
 *
 * A "moment" describes the operational context the current user is in so
 * surfaces can adapt their tone, emphasis, and information density.
 *
 * Moments (ordered by precedence — first match wins):
 *   conflict  – the user's pending request conflicts with team coverage
 *   returning – the user just came back from approved leave (≤7 days ago)
 *   peak      – team coverage for the current week is below 60%
 *   steady    – the quiet default; chip is hidden
 *
 * Density controls layout verbosity and is persisted per-user in localStorage.
 */
import { format, parseISO } from 'date-fns'

export type Moment = 'steady' | 'peak' | 'conflict' | 'returning'
export type Density = 'comfortable' | 'compact'

export interface OperationalContext {
  moment: Moment
  density: Density
  /** Human-readable explanation of why we're in this moment, e.g.
   *  "Q4 peak window · Nov 15 – Dec 31" or "You returned 2 days ago". */
  reason: string | null
}

// ── Computation inputs ────────────────────────────────────────────────────────

export interface ComputeMomentInput {
  /** Active blackouts that overlap today. Empty array = none. */
  activeBlackouts: ActiveBlackout[]
  /** The user's most recent approved leave, or null if none. */
  mostRecentApprovedLeave: RecentLeave | null
  /** Whether the user currently has a pending leave with conflicts. */
  hasPendingConflict: boolean
  /** Present-day coverage % for the current week (0-100).
   *  null = data not yet available (falls back to steady). */
  weekCoveragePct: number | null
}

export interface ActiveBlackout {
  name: string
  startDate: string
  endDate: string
}

export interface RecentLeave {
  /** ISO date string, e.g. "2026-05-03" */
  endDate: string
}

// ── Coverage threshold ────────────────────────────────────────────────────────

const PEAK_COVERAGE_THRESHOLD_PCT = 60

// ── Date helpers (pure, no side-effects) ─────────────────────────────────────

function todayIsoDate(): string {
  return new Date().toISOString().split('T')[0]!
}

/** Days between two ISO date strings (dateA - dateB). */
function daysBetween(a: string, b: string): number {
  const msPerDay = 86_400_000
  return Math.round((Date.parse(a) - Date.parse(b)) / msPerDay)
}

function formatDate(iso: string): string {
  // Day + abbreviated month, e.g. "15 May".
  return format(parseISO(iso), 'dd MMM')
}

// ── Pure computation ──────────────────────────────────────────────────────────

/**
 * Compute the current OperationalContext from available data inputs.
 *
 * Precedence (first match wins): conflict > returning > peak > steady.
 *
 * When data isn't available yet (loading), callers should omit the field
 * (null / empty array) — the function safely defaults to steady.
 */
export function computeMoment(input: ComputeMomentInput): OperationalContext {
  const {
    activeBlackouts,
    mostRecentApprovedLeave,
    hasPendingConflict,
    weekCoveragePct,
  } = input

  // 1. Conflict — pending leave has a scheduling conflict
  if (hasPendingConflict) {
    return {
      moment: 'conflict',
      density: readDensity(),
      reason: 'Your pending request has a team conflict — check coverage before submitting.',
    }
  }

  // 2. Returning — user's approved leave ended within the last 7 days
  if (mostRecentApprovedLeave !== null) {
    const today = todayIsoDate()
    const daysAgo = daysBetween(today, mostRecentApprovedLeave.endDate)
    if (daysAgo >= 0 && daysAgo <= 7) {
      const label = daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`
      return {
        moment: 'returning',
        density: readDensity(),
        reason: `You returned from leave ${label} — welcome back.`,
      }
    }
  }

  // 3. Peak — active blackout window OR team coverage <60%
  if (activeBlackouts.length > 0) {
    const first = activeBlackouts[0]!
    const label =
      first.name && first.startDate && first.endDate
        ? `${first.name} · ${formatDate(first.startDate)} – ${formatDate(first.endDate)}`
        : first.name || 'Active blackout window'
    return {
      moment: 'peak',
      density: readDensity(),
      reason: label,
    }
  }

  if (weekCoveragePct !== null && weekCoveragePct < PEAK_COVERAGE_THRESHOLD_PCT) {
    return {
      moment: 'peak',
      density: readDensity(),
      reason: `Team coverage is ${Math.round(weekCoveragePct)}% this week — please coordinate before applying.`,
    }
  }

  // 4. Steady — quiet default
  return {
    moment: 'steady',
    density: readDensity(),
    reason: null,
  }
}

// ── Density persistence ───────────────────────────────────────────────────────

const DENSITY_STORAGE_KEY = 'lms.density'

export function readDensity(): Density {
  if (typeof window === 'undefined') return 'comfortable'
  try {
    const raw = window.localStorage.getItem(DENSITY_STORAGE_KEY)
    if (raw === 'compact' || raw === 'comfortable') return raw
    return 'comfortable'
  } catch {
    return 'comfortable'
  }
}

export function writeDensity(density: Density): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DENSITY_STORAGE_KEY, density)
  } catch {
    // localStorage may throw in private mode; ignore — state still works in-memory
  }
}
